import MarkdownIt from 'markdown-it';

export type PolicyType = 'term' | 'legal' | 'privacy' | 'cookie';

export interface PolicyArticle {
  title: string;
  html: string;
}

export interface ParsedPolicy {
  date: string;
  headerHtml: string;
  introSections: string[];
  articles: PolicyArticle[];
}

const md = new MarkdownIt({
  html: true,
  linkify: false,
  breaks: false,
});

const mdIntro = new MarkdownIt({
  html: true,
  linkify: false,
  breaks: false,
});

const LIST_NUM_MARKER = '<!-- list:num -->';
const LIST_LINK_NUM_MARKER = '<!-- list:linkNum -->';
const LIST_NUM_CLASS = 'num';
const LIST_LINK_NUM_CLASS = 'linkNum';
const MARKER_TO_CLASS: Record<string, string> = {
  [LIST_NUM_MARKER]: LIST_NUM_CLASS,
  [LIST_LINK_NUM_MARKER]: LIST_LINK_NUM_CLASS,
};

const toRoman = (num: number): string => {
  const table: [number, string][] = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let n = num;
  let result = '';
  for (const [value, numeral] of table) {
    while (n >= value) {
      result += numeral;
      n -= value;
    }
  }
  return result;
};

// md : 마커가 선행한 ordered list만 ul로 렌더링하고 번호 스타일은 list_item_open에서 처리
const applyPolicyListMarkers = (parser: MarkdownIt) => {
  parser.core.ruler.after('block', 'policy_list_markers', (state) => {
    let pendingClass: string | null = null;

    for (let i = 0; i < state.tokens.length; i += 1) {
      const token = state.tokens[i];

      if (token.type === 'html_block') {
        const markerClass = MARKER_TO_CLASS[token.content.trim()];
        if (markerClass) {
          token.hidden = true;
          pendingClass = markerClass;
          continue;
        }
      }

      if (pendingClass && token.type === 'ordered_list_open') {
        const listClass = pendingClass;
        token.meta = { ...(token.meta ?? {}), policyListClass: listClass };

        const level = token.level;
        for (let j = i + 1; j < state.tokens.length; j += 1) {
          const childToken = state.tokens[j];
          if (childToken.type === 'ordered_list_close' && childToken.level === level) {
            childToken.meta = { ...(childToken.meta ?? {}), policyListClass: listClass };
            break;
          }
          if (childToken.type === 'list_item_open' && childToken.level === level + 1) {
            childToken.meta = { ...(childToken.meta ?? {}), policyListClass: listClass };
          }
        }

        pendingClass = null;
        continue;
      }

      if (pendingClass) {
        pendingClass = null;
      }
    }
  });
};

applyPolicyListMarkers(md);

md.renderer.rules.html_block = (tokens, idx) => (
  MARKER_TO_CLASS[tokens[idx].content.trim()] ? '' : tokens[idx].content
);

md.renderer.rules.ordered_list_open = (tokens, idx, options, _env, self) => {
  const listClass = tokens[idx].meta?.policyListClass;
  if (!listClass) return self.renderToken(tokens, idx, options);
  tokens[idx].tag = 'ul';
  tokens[idx].attrs = (tokens[idx].attrs ?? []).filter(([name]) => name !== 'start');
  tokens[idx].attrJoin('class', listClass);
  return self.renderToken(tokens, idx, options);
};
md.renderer.rules.ordered_list_close = (tokens, idx, options, _env, self) => {
  if (tokens[idx].meta?.policyListClass) tokens[idx].tag = 'ul';
  return self.renderToken(tokens, idx, options);
};
md.renderer.rules.list_item_open = (tokens, idx, options, _env, self) => {
  const token = tokens[idx];
  const opening = self.renderToken(tokens, idx, options);
  const listClass = token.meta?.policyListClass;
  if (!listClass) return opening;
  if (token.markup === '.' || token.markup === ')') {
    const num = parseInt(token.info, 10) || 1;
    const marker = listClass === LIST_LINK_NUM_CLASS ? `${num}.` : `${toRoman(num)}.`;
    return `${opening}<span>${marker}</span> `;
  }
  return opening;
};

// mdIntro : ul에 공통 클래스 추가
mdIntro.renderer.rules.bullet_list_open = (tokens, idx, options, _env, self) => {
  tokens[idx].attrJoin('class', 'list');
  return self.renderToken(tokens, idx, options);
};


const FALLBACK_LOCALE = 'en';
const POLICY_BASE_URL = '/policy-content';
const policyCache = new Map<string, Promise<string>>();

const fetchPolicyByLocale = (type: PolicyType, locale: string): Promise<string> => {
  const key = `${locale}/${type}`;
  let entry = policyCache.get(key);
  if (!entry) {
    entry = $fetch<string>(`${POLICY_BASE_URL}/${locale}/${type}.md`, {
      responseType: 'text',
    });
    policyCache.set(key, entry);
  }
  return entry;
};

const fetchPolicyRaw = async (type: PolicyType, locale: string): Promise<string> => {
  try {
    return await fetchPolicyByLocale(type, locale);
  } catch (err) {
    policyCache.delete(`${locale}/${type}`);
    if (locale === FALLBACK_LOCALE) throw err;
    return fetchPolicyByLocale(type, FALLBACK_LOCALE);
  }
};

const DATE_REGEX = /<p\s+class="date">([\s\S]*?)<\/p>/i;
const EMPTY_THEAD_REGEX = /<thead>\s*<tr>([\s\S]*?)<\/tr>\s*<\/thead>\s*/gi;
const THEAD_CELL_REGEX = /<th(?:\s[^>]*)?>([\s\S]*?)<\/th>/gi;
const HTML_TAG_REGEX = /<[^>]*>/g;
const HTML_SPACE_ENTITY_REGEX = /&(?:nbsp|#160|#xA0);/gi;
const MULTILINE_TABLE_CELL_REGEX = /\{\{:\s*\r?\n([\s\S]*?)\r?\n\s*:\}\}/g;
const ANCHOR_OPEN_TAG_REGEX = /<a\b([^>]*)>/gi;
const HREF_ATTR_REGEX = /\bhref\s*=\s*(["'])(.*?)\1/i;
const TARGET_ATTR_REGEX = /\btarget\s*=/i;
const REL_ATTR_REGEX = /\brel\s*=\s*(["'])(.*?)\1/i;
const NEW_TAB_REL_VALUES = ['noopener', 'noreferrer'];

const hasTableHeadContent = (rowHtml: string): boolean => {
  THEAD_CELL_REGEX.lastIndex = 0;
  let hasCell = false;
  let match: RegExpExecArray | null;

  while ((match = THEAD_CELL_REGEX.exec(rowHtml)) !== null) {
    hasCell = true;
    const cellText = match[1]
      .replace(HTML_TAG_REGEX, '')
      .replace(HTML_SPACE_ENTITY_REGEX, '')
      .trim();

    if (cellText.length > 0) {
      return true;
    }
  }

  return !hasCell && rowHtml.replace(HTML_TAG_REGEX, '').trim().length > 0;
};

const removeEmptyTableHeads = (html: string): string => html.replace(
  EMPTY_THEAD_REGEX,
  (_thead, rowHtml: string) => hasTableHeadContent(rowHtml) ? _thead : '',
);

const normalizeMultilineTableCells = (source: string): string => source.replace(
  MULTILINE_TABLE_CELL_REGEX,
  (_block, cellContent: string) => cellContent
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .join(''),
);

const isMailtoLink = (href: string): boolean => href.trim().toLowerCase().startsWith('mailto:');
const isFragmentLink = (href: string): boolean => href.trim().startsWith('#');

const withNewTabRel = (attrs: string): string => {
  const relMatch = REL_ATTR_REGEX.exec(attrs);
  if (!relMatch) {
    return `${attrs} rel="noopener noreferrer"`;
  }

  const relValues = new Set(relMatch[2].split(/\s+/).filter(Boolean));
  for (const value of NEW_TAB_REL_VALUES) {
    relValues.add(value);
  }

  return attrs.replace(REL_ATTR_REGEX, `rel=${relMatch[1]}${Array.from(relValues).join(' ')}${relMatch[1]}`);
};

const openNonMailtoLinksInNewTab = (html: string): string => html.replace(
  ANCHOR_OPEN_TAG_REGEX,
  (anchorTag, attrs: string) => {
    const hrefMatch = HREF_ATTR_REGEX.exec(attrs);
    if (!hrefMatch || isMailtoLink(hrefMatch[2]) || isFragmentLink(hrefMatch[2])) {
      return anchorTag;
    }

    const targetAttrs = TARGET_ATTR_REGEX.test(attrs) ? attrs : `${attrs} target="_blank"`;
    return `<a${withNewTabRel(targetAttrs)}>`;
  },
);

const renderPolicyHtml = (source: string, parser = md): string => openNonMailtoLinksInNewTab(
  removeEmptyTableHeads(parser.render(normalizeMultilineTableCells(source), { useTableHeader: false })),
);

const parsePolicy = (raw: string): ParsedPolicy => {
  const lines = raw.split(/\r?\n/);
  const introLines: string[] = [];
  const articles: { title: string; bodyLines: string[] }[] = [];
  let current: { title: string; bodyLines: string[] } | null = null;

  for (const line of lines) {
    const headingMatch = /^##\s+(.+?)\s*$/.exec(line);
    if (headingMatch) {
      if (current) articles.push(current);
      current = { title: headingMatch[1].trim(), bodyLines: [] };
      continue;
    }
    if (current) {
      current.bodyLines.push(line);
    } else {
      introLines.push(line);
    }
  }
  if (current) articles.push(current);

  const introChunks: string[][] = [[]];
  for (const line of introLines) {
    if (/^---\s*$/.test(line)) {
      introChunks.push([]);
    } else {
      introChunks[introChunks.length - 1].push(line);
    }
  }

  const [headerChunk = [], ...sectionChunks] = introChunks;
  let headerSource = normalizeMultilineTableCells(headerChunk.join('\n').trim());
  let date = '';
  const dateMatch = DATE_REGEX.exec(headerSource);
  if (dateMatch) {
    date = dateMatch[1].trim();
    headerSource = headerSource.replace(dateMatch[0], '').trim();
  }

  return {
    date,
    headerHtml: headerSource ? renderPolicyHtml(headerSource) : '',
    introSections: sectionChunks
      .map(c => c.join('\n').trim())
      .filter(s => s.length > 0)
      .map(s => renderPolicyHtml(s, mdIntro)),
    articles: articles.map(a => ({
      title: a.title,
      html: renderPolicyHtml(a.bodyLines.join('\n').trim()),
    })),
  };
};

export const usePolicyMarkdown = (type: PolicyType) => {
  const { locale } = useI18n();
  const policy = ref<ParsedPolicy | null>(null);
  const isLoading = ref(true);

  const load = async () => {
    isLoading.value = true;
    try {
      const raw = await fetchPolicyRaw(type, locale.value);
      policy.value = parsePolicy(raw);
    } catch {
      policy.value = parsePolicy('');
    } finally {
      isLoading.value = false;
    }
  };

  if (import.meta.client) {
    onMounted(load);
    watch(() => locale.value, () => {
      if (policy.value !== null) load();
    });
  }

  return { policy, isLoading };
};
